import { CommonModule } from '@angular/common';
import { Component, input, output, signal, inject, computed } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CheckCheck, Info } from 'lucide-angular';
import { AccordionModule } from 'primeng/accordion';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonComponent } from 'src/app/@core/components/ui/button/button.component';
import { IconComponent } from 'src/app/@core/components/ui/icon/icon.component';
import { DynamicComponentRenderer } from 'src/app/@shared/directives/dynamic-component-renderer.directive';
import { MarkdownRenderPipe } from 'src/app/@shared/pipes/markdown-render.pipe';
import { MessageItem, THREAD_METADATA_STATUS } from '../../../../models/model';
import { SidePanelService } from '../../../../services/side-panel.service';
import { AttachmentGroupComponent } from '../../../../../../@shared/components/attachment-group/attachment-group.component';
import { ThreadMessagesService } from '../../../../services/thread-messages.service';
import { AttachmentService } from 'src/app/contract-management/services/attachment.service';
import { ToastService } from 'src/app/@core/services/toast.service';

@Component({
    selector: 'app-human-message',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        AccordionModule,
        ButtonComponent,
        IconComponent,
        MarkdownRenderPipe,
        DynamicComponentRenderer,
        TooltipModule,
        AttachmentGroupComponent,
    ],
    templateUrl: './human-message.component.html',
    styleUrl: './human-message.component.scss'
})
export class HumanMessageComponent {
    // Inputs
    message = input.required<MessageItem>();
    isOwner = input<boolean>(false);
    canEdit = input<boolean>(false);
    isPublicView = input<boolean>(false);

    sidePanelService = inject(SidePanelService);
    attachmentService = inject(AttachmentService);
    toastService = inject(ToastService);
    threadMessagesService = inject(ThreadMessagesService);
    THREAD_METADATA_STATUS = THREAD_METADATA_STATUS;

    // Outputs
    edit = output<{ messageId: string, content: string }>();
    copy = output<string>();
    /**
     * Emitted when the user clicks "Save to Library" on the Enhanced Prompt accordion.
     * Carries the message ID and markdown so the parent can pre-fill the save dialog
     * and later write the resulting prompt _id back onto this message.
     */
    saveEnhancement = output<{ messageId: string; markdown: string }>();

    /** Characters threshold above which the bubble collapses by default */
    private readonly COLLAPSE_THRESHOLD = 300;

    // State
    isEditing = signal<boolean>(false);
    editedContent = signal<string>('');
    isCopied = signal<boolean>(false);
    isCollapsed = signal<boolean>(true);

    /** True when the raw message text exceeds the collapse threshold */
    isLong = computed(() => this.message().content.length > this.COLLAPSE_THRESHOLD);

    // ── Enhanced Prompt (approved_enhancement) ──────────────────────────────

    /**
     * Extracts the approved_enhancement from the message's additional_kwargs.
     * Returns the object only when kind === 'PROMPT_ENHANCEMENT', otherwise null.
     */
    approvedEnhancement = computed<{ markdown: string; source: string; prompt_id?: string } | null>(() => {
        const msg = this.message() as any;
        const ae = msg?.['additional_kwargs']?.approved_enhancement;
        if (ae?.kind === 'PROMPT_ENHANCEMENT' && ae?.markdown) {
            return ae as { markdown: string; source: string; prompt_id?: string };
        }
        return null;
    });

    // Icons
    CheckCheck = CheckCheck;
    Info = Info;

    startEditing() {
        this.editedContent.set(this.message().content);
        this.isEditing.set(true);
    }

    cancelEditing() {
        this.isEditing.set(false);
        this.editedContent.set('');
    }

    saveEdit() {
        const content = this.editedContent().trim();
        if (content) {
            this.edit.emit({ messageId: this.message().id, content });
            this.cancelEditing();
        }
    }

    toggleCollapse() {
        this.isCollapsed.update(v => !v);
    }

    copyMessage() {
        navigator.clipboard.writeText(this.message().content).then(() => {
            this.isCopied.set(true);
            setTimeout(() => this.isCopied.set(false), 2000);
            this.copy.emit(this.message().id);
        });
    }

    handleKeyDown(event: KeyboardEvent) {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.saveEdit();
        }
        if (event.key === 'Escape') {
            this.cancelEditing();
        }
    }

    /**
     * Emits the enhanced prompt markdown up the component tree so the parent
     * (active-thread) can open the "Save as Prompt" dialog pre-filled with the
     * enhanced content and defaulting to PERSONAL access level.
     */
    saveEnhancementToLibrary(): void {
        const enh = this.approvedEnhancement();
        if (enh?.markdown) {
            this.saveEnhancement.emit({ messageId: this.message().id, markdown: enh.markdown });
        }
    }

    onAttachmentOpen(attachment: any) {
        const attachmentId = attachment._id ?? attachment.attachmentId;
        if (!attachmentId) {
            this.toastService.error('Cannot open file: missing attachment id');
            return;
        }
        this.attachmentService.getAttachmentWithSignedUrl(attachmentId).subscribe({
            next: (res) => {
                if (res.success && res.data) {
                    this.sidePanelService.open('FILE_PREVIEW', { file: res.data, documentId: res.data._id });
                } else {
                    this.toastService.error('Failed to load file preview');
                }
            },
            error: (err) => {
                this.toastService.error('Failed to load file preview: ' + (err?.message ?? 'Unknown error'));
            }
        });
    }
}
